import { TransactionResponse } from '../../model/Transaction.model';
import axiosInstance from '../../utils/axiosInstance';

// Interface for maturity schedule transaction
export interface MaturityScheduleTransactionRequest {
  maturityScheduleId: number;
  creditCardId?: number;
  posDeviceId?: number;
  transactionDate: string; // YYYY-MM-DD format
  amount: number;
  transactionType: 'deposit' | 'withdrawal';
  feeForCustomer?: number;
  customerType?: '%' | 'vn'; // Loại phí khách hàng: % hoặc VND
  feeApplied?: number;
  commissionRateForPartner?: number; // Phí CTV (%)
  status?: 'PENDING' | 'COMPLETED';
  returnToCompany?: boolean;
  transactionFor?: 'withdrawal' | 'maturity';
}

// Interface for withdrawal service transaction
export interface WithdrawalServiceTransactionRequest {
  creditCardId: number;
  posDeviceId: number;
  transactionDate: string; // YYYY-MM-DD format
  amount: number;
  transactionType: 'withdrawal';
  feeForCustomer?: number;
  customerType?: '%' | 'vn'; // Loại phí khách hàng: % hoặc VND
  feeApplied?: number;
  commissionRateForPartner?: number; // Phí CTV (%)
  status?: 'PENDING' | 'COMPLETED';
  returnToCompany?: boolean;
}

// Interface for API response
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error?: string;
}

class TransactionService {
  private readonly basePath = '/transaction';

  // Create transaction for maturity schedule
  async createTransactionForMaturitySchedule(
    request: MaturityScheduleTransactionRequest,
    employeeId: number
  ): Promise<ApiResponse<TransactionResponse>> {
    const response = await axiosInstance.post(
      `${this.basePath}/create-for-maturity?employeeId=${employeeId}`,
      request
    );
    return response.data;
  }

  // Create transaction for withdrawal service
  async createTransactionForWithdrawal(
    request: MaturityScheduleTransactionRequest,
    employeeId: number
  ): Promise<ApiResponse<TransactionResponse>> {
    const response = await axiosInstance.post(
      `${this.basePath}/create-for-withdrawal?employeeId=${employeeId}`,
      request
    );
    return response.data;
  }

  // Update transaction status
  async updateTransactionStatus(
    transactionId: number,
    status: 'PENDING' | 'COMPLETED'
  ): Promise<ApiResponse<TransactionResponse>> {
    const response = await axiosInstance.put(
      `${this.basePath}/${transactionId}/status?status=${status}`
    );
    return response.data;
  }

  // Update withdrawal transaction
  async updateWithdrawalTransaction(
    transactionId: number,
    request: MaturityScheduleTransactionRequest,
    employeeId: number
  ): Promise<ApiResponse<TransactionResponse>> {
    const response = await axiosInstance.put(
      `${this.basePath}/withdrawal/${transactionId}?employeeId=${employeeId}`,
      request
    );
    return response.data;
  }

  // Get transactions by employee
  async getTransactionsByEmployee(
    employeeId: number,
    page: number = 0,
    size: number = 10
  ): Promise<ApiResponse<any>> {
    const response = await axiosInstance.get(
      `${this.basePath}/by-employee/${employeeId}?page=${page}&size=${size}`
    );
    return response.data;
  }

  // Get transactions by maturity schedule
  async getTransactionsByMaturitySchedule(
    maturityScheduleId: number
  ): Promise<ApiResponse<TransactionResponse[]>> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/by-maturity-schedule/${maturityScheduleId}`
      );

      const data = response.data;
      if (!data) {
        return {
          success: true,
          message: 'No transactions found',
          data: [],
        };
      }

      // Ensure data array exists
      if (!data.data) {
        data.data = [];
      }

      return data;
    } catch (error: any) {
      console.error('Error getting transactions by maturity schedule:', error);

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          success: true,
          message: 'No transactions found',
          data: [],
        };
      }

      throw error;
    }
  }

  // Get transactions by maturity schedule with employee info
  async getTransactionsByMaturityScheduleWithEmployee(
    maturityScheduleId: number
  ): Promise<ApiResponse<TransactionResponse[]>> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/by-maturity-schedule/${maturityScheduleId}/with-employee`
      );

      const data = response.data;
      if (!data) {
        return {
          success: true,
          message: 'No transactions found',
          data: [],
        };
      }

      // Ensure data array exists
      if (!data.data) {
        data.data = [];
      }

      return data;
    } catch (error: any) {
      console.error(
        'Error getting transactions by maturity schedule with employee:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          success: true,
          message: 'No transactions found',
          data: [],
        };
      }

      throw error;
    }
  }

  // Get deleted transactions by maturity schedule with employee info
  async getDeletedTransactionsByMaturityScheduleWithEmployee(
    maturityScheduleId: number
  ): Promise<ApiResponse<TransactionResponse[]>> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/deleted/by-maturity-schedule/${maturityScheduleId}`
      );

      const data = response.data;
      if (!data) {
        return {
          success: true,
          message: 'No deleted transactions found',
          data: [],
        };
      }

      // Ensure data array exists
      if (!data.data) {
        data.data = [];
      }

      return data;
    } catch (error: any) {
      console.error(
        'Error getting deleted transactions by maturity schedule with employee:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          success: true,
          message: 'No deleted transactions found',
          data: [],
        };
      }

      throw error;
    }
  }

  // Get transaction detail
  async getTransactionDetail(
    transactionId: number
  ): Promise<ApiResponse<TransactionResponse>> {
    const response = await axiosInstance.get(
      `${this.basePath}/${transactionId}`
    );
    return response.data;
  }

  // Get transactions by type
  async getTransactionsByType(
    transactionFor: 'withdrawal' | 'maturity',
    page: number = 0,
    size: number = 10
  ): Promise<ApiResponse<any>> {
    const response = await axiosInstance.get(
      `${this.basePath}/by-type/${transactionFor}?page=${page}&size=${size}`
    );
    return response.data;
  }

  // Soft delete transaction
  async softDeleteTransaction(
    transactionId: number,
    employeeId: number
  ): Promise<ApiResponse<TransactionResponse>> {
    const response = await axiosInstance.delete(
      `${this.basePath}/${transactionId}/soft-delete?employeeId=${employeeId}`
    );
    return response.data;
  }

  // Toggle withdrawal status (PENDING <-> COMPLETED)
  async toggleWithdrawalStatus(
    transactionId: number
  ): Promise<ApiResponse<TransactionResponse>> {
    const response = await axiosInstance.put(
      `${this.basePath}/${transactionId}/toggle-withdrawal-status`
    );
    return response.data;
  }

  /**
   * Lấy danh sách giao dịch withdrawal của dịch vụ đáo hạn (maturity)
   */
  async getMaturityWithdrawalTransactions(
    page: number = 0,
    size: number = 20
  ): Promise<{
    content: any[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/by-type/maturity`,
        {
          params: { page, size },
        }
      );

      // Safely access response data
      const responseData = response.data?.data;
      if (!responseData) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      // Filter chỉ lấy withdrawal transactions từ response data
      const allTransactions = responseData.content || [];
      const filteredContent = allTransactions.filter(
        (transaction: any) => transaction.transactionType === 'withdrawal'
      );

      return {
        ...responseData,
        content: filteredContent,
      };
    } catch (error: any) {
      console.error('Error fetching maturity withdrawal transactions:', error);

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch withdrawal'
      );
    }
  }

  /**
   * Lấy danh sách giao dịch withdrawal của dịch vụ đáo hạn (maturity) với thông tin employee
   */
  async getMaturityWithdrawalTransactionsWithEmployee(
    page: number = 0,
    size: number = 20
  ): Promise<{
    content: any[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/by-type/maturity/with-employee`,
        {
          params: { page, size },
        }
      );

      // Safely access response data
      const responseData = response.data?.data;
      if (!responseData) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      // Filter chỉ lấy withdrawal transactions từ response data
      const allTransactions = responseData.content || [];
      const filteredContent = allTransactions.filter(
        (transaction: any) => transaction.transactionType === 'withdrawal'
      );

      return {
        ...responseData,
        content: filteredContent,
      };
    } catch (error: any) {
      console.error(
        'Error fetching maturity withdrawal transactions with employee:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch withdrawal với thông tin nhân viên'
      );
    }
  }

  /**
   * Lấy danh sách giao dịch đáo hạn theo tháng với thông tin employee
   */
  async getMaturityTransactionsByDate(
    year: number,
    month: number,
    day?: number,
    page: number = 0,
    size: number = 20
  ): Promise<{
    content: any[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/maturity/by-date`,
        {
          params: { year, month, day, page, size },
        }
      );

      // Safely access response data
      const responseData = response.data?.data;
      if (!responseData) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      return responseData;
    } catch (error: any) {
      console.error('Error fetching maturity transactions by date:', error);

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch đáo hạn theo ngày'
      );
    }
  }

  /**
   * Lấy danh sách giao dịch withdrawal service theo tháng (TẤT CẢ - không phân trang)
   * Để frontend tự xử lý filtering và pagination
   */
  async getWithdrawalServiceTransactionsByMonth(
    year: number,
    month: number,
    page: number = 0,
    size: number = 1000, // Lấy số lượng lớn để có tất cả data trong tháng
    filters?: {
      employeeName?: string;
      cardNumber?: string;
      posDeviceCode?: string;
      status?: string;
      amountFilter?: string;
      feeApplied?: string;
      balance?: string;
    }
  ): Promise<{
    content: any[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }> {
    try {
      const params: any = { year, month, page: 0, size: 1000 }; // Luôn lấy từ page 0 với size lớn

      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal-service/by-month`,
        { params }
      );

      // Safely access response data
      const responseData = response.data?.data;
      if (!responseData) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      return responseData;
    } catch (error: any) {
      console.error(
        'Error fetching withdrawal service transactions by month:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch withdrawal service theo tháng'
      );
    }
  }

  /**
   * Lấy danh sách giao dịch withdrawal service theo tháng và nhân viên (TẤT CẢ - không phân trang)
   */
  async getWithdrawalServiceTransactionsByMonthAndEmployee(
    year: number,
    month: number,
    employeeId: number,
    page: number = 0,
    size: number = 1000, // Lấy số lượng lớn
    filters?: {
      employeeName?: string;
      cardNumber?: string;
      posDeviceCode?: string;
      status?: string;
      amountFilter?: string;
      feeApplied?: string;
      balance?: string;
    }
  ): Promise<{
    content: any[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }> {
    try {
      const params: any = { year, month, employeeId, page: 0, size: 1000 };

      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal-service/by-month-and-employee`,
        { params }
      );

      // Safely access response data
      const responseData = response.data?.data;
      if (!responseData) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      return responseData;
    } catch (error: any) {
      console.error(
        'Error fetching withdrawal service transactions by month and employee:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch withdrawal service theo tháng và nhân viên'
      );
    }
  }

  /**
   * Lấy danh sách giao dịch đáo hạn theo tháng và nhân viên (cho ROLE_EMPLOYEE)
   */
  async getMaturityTransactionsByMonthAndEmployee(
    year: number,
    month: number,
    day: number,
    employeeId: number,
    page: number = 0,
    size: number = 20
  ): Promise<{
    content: any[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/maturity/by-month-and-employee`,
        {
          params: { day, year, month, employeeId, page, size },
        }
      );

      // Safely access response data
      const responseData = response.data?.data;
      if (!responseData) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      return responseData;
    } catch (error: any) {
      console.error(
        'Error fetching maturity transactions by month and employee:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch đáo hạn theo tháng và nhân viên'
      );
    }
  }

  /**
   * Lấy danh sách giao dịch withdrawal service đã xóa theo tháng (cho Admin)
   */
  async getDeletedWithdrawalServiceTransactionsByMonth(
    year: number,
    month: number,
    page: number = 0,
    size: number = 20
  ): Promise<{
    content: any[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal-service/deleted/by-month`,
        {
          params: { year, month, page, size },
        }
      );

      // Safely access response data
      const responseData = response.data?.data;
      if (!responseData) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      return responseData;
    } catch (error: any) {
      console.error(
        'Error fetching deleted withdrawal service transactions by month:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch withdrawal service đã xóa theo tháng'
      );
    }
  }

  /**
   * Lấy danh sách giao dịch withdrawal service đã xóa theo tháng và nhân viên (cho Employee)
   */
  async getDeletedWithdrawalServiceTransactionsByMonthAndEmployee(
    year: number,
    month: number,
    employeeId: number,
    page: number = 0,
    size: number = 20
  ): Promise<{
    content: any[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal-service/deleted/by-month-and-employee`,
        {
          params: { year, month, employeeId, page, size },
        }
      );

      // Safely access response data
      const responseData = response.data?.data;
      if (!responseData) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      return responseData;
    } catch (error: any) {
      console.error(
        'Error fetching deleted withdrawal service transactions by month and employee:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
          size: size,
        };
      }

      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch withdrawal service đã xóa theo tháng và nhân viên'
      );
    }
  }

  async getMaturityTransactionsByMonthYear(year: number, month: number) {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/maturity/by-month-year`,
        {
          params: { year, month },
        }
      );
      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Lấy danh sách giao dịch withdrawal theo tháng (không có filtering - sử dụng bộ lọc của bảng)
   */
  async getWithdrawalTransactionsByMonth(
    year: number,
    month: number,
    page: number = 0,
    size: number = 20,
    sortBy: string = 'transactionDate',
    sortDir: string = 'desc'
  ) {
    try {
      const response = await axiosInstance.get(
        `/transaction/withdrawal/${year}/${month}`,
        {
          params: { page, size, sortBy, sortDir },
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching withdrawal transactions by month:', error);
      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch withdrawal'
      );
    }
  }

  /**
   * Lấy danh sách giao dịch withdrawal theo tháng và nhân viên (không có filtering - sử dụng bộ lọc của bảng)
   */
  async getWithdrawalTransactionsByMonthAndEmployee(
    year: number,
    month: number,
    employeeId: number,
    page: number = 0,
    size: number = 20,
    sortBy: string = 'transactionDate',
    sortDir: string = 'desc'
  ) {
    try {
      const response = await axiosInstance.get(
        `/transaction/withdrawal/${year}/${month}/employee/${employeeId}`,
        {
          params: { page, size, sortBy, sortDir },
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error fetching withdrawal transactions by month and employee:',
        error
      );
      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch withdrawal theo nhân viên'
      );
    }
  }

  /**
   * Tạo nhiều giao dịch withdrawal cùng lúc (bulk create)
   */
  async createBulkWithdrawalTransactions(
    requests: any[],
    employeeId: number
  ): Promise<ApiResponse<any[]>> {
    try {
      const response = await axiosInstance.post(
        `${this.basePath}/bulk-create-withdrawal?employeeId=${employeeId}`,
        requests
      );
      return response.data;
    } catch (error: any) {
      console.error('Error creating bulk withdrawal transactions:', error);
      console.error('Request data:', requests);
      console.error('Full error:', error.response || error);
      throw new Error(
        error.response?.data?.message ||
          'Không thể tạo các giao dịch withdrawal'
      );
    }
  }

  /**
   * Tạo giao dịch rút tiền cho thẻ cụ thể
   */
  async createWithdrawalTransactionForCard(
    request: WithdrawalServiceTransactionRequest,
    employeeId: number
  ): Promise<ApiResponse<TransactionResponse>> {
    try {
      const response = await axiosInstance.post(
        `${this.basePath}/withdrawal-card?employeeId=${employeeId}`,
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Error creating withdrawal transaction for card:', error);
      throw new Error(
        error.response?.data?.message || 'Không thể tạo giao dịch rút tiền'
      );
    }
  }

  /**
   * Lấy TẤT CẢ giao dịch withdrawal service theo tháng (không phân trang) - cho frontend filtering
   */
  async getAllWithdrawalServiceTransactionsByMonth(
    year: number,
    month: number
  ): Promise<any[]> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal-service/by-month`,
        {
          params: { year, month, page: 0, size: 10000 }, // Lấy một số lượng rất lớn
        }
      );

      const responseData = response.data?.data;
      if (!responseData || !responseData.content) {
        return [];
      }

      return responseData.content;
    } catch (error: any) {
      console.error(
        'Error fetching all withdrawal service transactions by month:',
        error
      );
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return [];
      }

      // Fallback: nếu API mới không hoạt động, thử API cũ với pagination size lớn
      try {
        console.warn('Fallback to old API method');
        const fallbackResponse =
          await this.getWithdrawalServiceTransactionsByMonth(
            year,
            month,
            0,
            1000
          );
        return fallbackResponse.content || [];
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        throw new Error(
          error.response?.data?.message ||
            'Không thể tải danh sách giao dịch withdrawal service'
        );
      }
    }
  }

  /**
   * Lấy TẤT CẢ giao dịch withdrawal service theo tháng và nhân viên (không phân trang) - cho frontend filtering
   */
  async getAllWithdrawalServiceTransactionsByMonthAndEmployee(
    year: number,
    month: number,
    employeeId: number
  ): Promise<any[]> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal-service/by-month-and-employee`,
        {
          params: { year, month, employeeId, page: 0, size: 10000 },
        }
      );

      const responseData = response.data?.data;
      if (!responseData || !responseData.content) {
        return [];
      }

      return responseData.content;
    } catch (error: any) {
      console.error(
        'Error fetching all withdrawal service transactions by month and employee:',
        error
      );
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return [];
      }

      // Fallback: nếu API mới không hoạt động, thử API cũ với pagination size lớn
      try {
        console.warn('Fallback to old API method');
        const fallbackResponse =
          await this.getWithdrawalServiceTransactionsByMonthAndEmployee(
            year,
            month,
            employeeId,
            0,
            1000
          );
        return fallbackResponse.content || [];
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        throw new Error(
          error.response?.data?.message ||
            'Không thể tải danh sách giao dịch withdrawal service theo nhân viên'
        );
      }
    }
  }

  /**
   * Lấy TẤT CẢ giao dịch withdrawal service đã xóa theo tháng (không phân trang) - cho frontend filtering
   */
  async getAllDeletedWithdrawalServiceTransactionsByMonth(
    year: number,
    month: number
  ): Promise<any[]> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal-service/deleted/by-month`,
        {
          params: { year, month, page: 0, size: 10000 },
        }
      );

      const responseData = response.data?.data;
      if (!responseData || !responseData.content) {
        return [];
      }

      return responseData.content;
    } catch (error: any) {
      console.error(
        'Error fetching all deleted withdrawal service transactions by month:',
        error
      );
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return [];
      }

      // Fallback: nếu API mới không hoạt động, thử API cũ với pagination size lớn
      try {
        console.warn('Fallback to old API method');
        const fallbackResponse =
          await this.getDeletedWithdrawalServiceTransactionsByMonth(
            year,
            month,
            0,
            1000
          );
        return fallbackResponse.content || [];
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        throw new Error(
          error.response?.data?.message ||
            'Không thể tải danh sách giao dịch withdrawal service đã xóa'
        );
      }
    }
  }

  /**
   * Lấy TẤT CẢ giao dịch withdrawal service đã xóa theo tháng và nhân viên (không phân trang) - cho frontend filtering
   */
  async getAllDeletedWithdrawalServiceTransactionsByMonthAndEmployee(
    year: number,
    month: number,
    employeeId: number
  ): Promise<any[]> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal-service/deleted/by-month-and-employee`,
        {
          params: { year, month, employeeId, page: 0, size: 10000 },
        }
      );

      const responseData = response.data?.data;
      if (!responseData || !responseData.content) {
        return [];
      }

      return responseData.content;
    } catch (error: any) {
      console.error(
        'Error fetching all deleted withdrawal service transactions by month and employee:',
        error
      );
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return [];
      }

      // Fallback: nếu API mới không hoạt động, thử API cũ với pagination size lớn
      try {
        console.warn('Fallback to old API method');
        const fallbackResponse =
          await this.getDeletedWithdrawalServiceTransactionsByMonthAndEmployee(
            year,
            month,
            employeeId,
            0,
            1000
          );
        return fallbackResponse.content || [];
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        throw new Error(
          error.response?.data?.message ||
            'Không thể tải danh sách giao dịch withdrawal service đã xóa theo nhân viên'
        );
      }
    }
  }

  /**
   * Lấy danh sách giao dịch withdrawal theo thẻ tín dụng
   */
  async getWithdrawalTransactionsByCreditCard(
    creditCardId: number
  ): Promise<ApiResponse<TransactionResponse[]>> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal/by-credit-card/${creditCardId}`
      );

      const data = response.data;
      if (!data) {
        return {
          success: true,
          message: 'No withdrawal transactions found',
          data: [],
        };
      }

      // Ensure data array exists
      if (!data.data) {
        data.data = [];
      }

      return data;
    } catch (error: any) {
      console.error(
        'Error getting withdrawal transactions by credit card:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          success: true,
          message: 'No withdrawal transactions found',
          data: [],
        };
      }

      throw error;
    }
  }

  /**
   * Lấy danh sách giao dịch withdrawal theo thẻ tín dụng và năm
   */
  async getWithdrawalTransactionsByCreditCardAndYear(
    creditCardId: number,
    year: number
  ): Promise<ApiResponse<TransactionResponse[]>> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal/by-credit-card/${creditCardId}/year/${year}`
      );

      const data = response.data;
      if (!data) {
        return {
          success: true,
          message: 'No withdrawal transactions found',
          data: [],
        };
      }

      // Ensure data array exists
      if (!data.data) {
        data.data = [];
      }

      return data;
    } catch (error: any) {
      console.error(
        'Error getting withdrawal transactions by credit card and year:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          success: true,
          message: 'No withdrawal transactions found',
          data: [],
        };
      }

      throw error;
    }
  }

  /**
   * Lấy danh sách giao dịch withdrawal đã xóa theo thẻ tín dụng
   */
  async getDeletedWithdrawalTransactionsByCreditCard(
    creditCardId: number
  ): Promise<ApiResponse<TransactionResponse[]>> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal/deleted/by-credit-card/${creditCardId}`
      );

      const data = response.data;
      if (!data) {
        return {
          success: true,
          message: 'No deleted withdrawal transactions found',
          data: [],
        };
      }

      // Ensure data array exists
      if (!data.data) {
        data.data = [];
      }

      return data;
    } catch (error: any) {
      console.error(
        'Error getting deleted withdrawal transactions by credit card:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          success: true,
          message: 'No deleted withdrawal transactions found',
          data: [],
        };
      }

      throw error;
    }
  }

  /**
   * Lấy danh sách giao dịch withdrawal đã xóa theo thẻ tín dụng và năm
   */
  async getDeletedWithdrawalTransactionsByCreditCardAndYear(
    creditCardId: number,
    year: number
  ): Promise<ApiResponse<TransactionResponse[]>> {
    try {
      const response = await axiosInstance.get(
        `${this.basePath}/withdrawal/deleted/by-credit-card/${creditCardId}/year/${year}`
      );

      const data = response.data;
      if (!data) {
        return {
          success: true,
          message: 'No deleted withdrawal transactions found',
          data: [],
        };
      }

      // Ensure data array exists
      if (!data.data) {
        data.data = [];
      }

      return data;
    } catch (error: any) {
      console.error(
        'Error getting deleted withdrawal transactions by credit card and year:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          success: true,
          message: 'No deleted withdrawal transactions found',
          data: [],
        };
      }

      throw error;
    }
  }

  /**
   * Lấy TẤT CẢ giao dịch withdrawal theo tháng (không phân trang) - cho frontend pagination với Antd Table
   */
  async getAllWithdrawalTransactionsByMonth(
    year: number,
    month: number
  ): Promise<any[]> {
    try {
      const response = await axiosInstance.get(
        `/transaction/withdrawal/all/${year}/${month}`
      );

      const responseData = response.data?.data;
      if (!responseData) {
        return [];
      }

      // Response trả về List<TransactionResponseDto> trực tiếp, không cần .content
      return Array.isArray(responseData) ? responseData : [];
    } catch (error: any) {
      console.error(
        'Error fetching all withdrawal transactions by month:',
        error
      );
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return [];
      }

      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch withdrawal'
      );
    }
  }

  /**
   * Lấy TẤT CẢ giao dịch withdrawal theo tháng và nhân viên (không phân trang) - cho frontend pagination với Antd Table
   */
  async getAllWithdrawalTransactionsByMonthAndEmployee(
    year: number,
    month: number,
    employeeId: number
  ): Promise<any[]> {
    try {
      const response = await axiosInstance.get(
        `/transaction/withdrawal/all/${year}/${month}/employee/${employeeId}`
      );

      const responseData = response.data?.data;
      if (!responseData) {
        return [];
      }

      // Response trả về List<TransactionResponseDto> trực tiếp, không cần .content
      return Array.isArray(responseData) ? responseData : [];
    } catch (error: any) {
      console.error(
        'Error fetching all withdrawal transactions by month and employee:',
        error
      );
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return [];
      }

      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch withdrawal theo nhân viên'
      );
    }
  }

  /**
   * Lấy danh sách giao dịch withdrawal service có trả tiền sai cho khách theo tháng
   * @param month Tháng (1-12)
   * @param year Năm
   * @returns Danh sách giao dịch có chênh lệch thanh toán >= 51000 VND
   */
  async getWrongPaymentWithdrawalServiceTransactions(
    month: number,
    year: number
  ): Promise<any[]> {
    try {
      const response = await axiosInstance.get(
        '/transaction/withdrawal-service/wrong-payment',
        {
          params: { month, year },
        }
      );

      const responseData = response.data?.data;
      if (!responseData) {
        return [];
      }

      return Array.isArray(responseData) ? responseData : [];
    } catch (error: any) {
      console.error(
        'Error fetching wrong payment withdrawal service transactions:',
        error
      );
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return [];
      }

      throw new Error(
        error.response?.data?.message ||
          'Không thể tải danh sách giao dịch có thanh toán sai'
      );
    }
  }

  /**
   * Đánh dấu đã kiểm tra thanh toán cho withdrawal service transaction
   * @param transactionId ID của transaction
   */
  async checkPaymentWithdrawalService(transactionId: number): Promise<void> {
    try {
      await axiosInstance.patch(
        `/transaction/withdrawal-service/${transactionId}/check-payment`
      );
    } catch (error: any) {
      console.error(
        'Error checking payment for withdrawal service transaction:',
        error
      );
      throw new Error(
        error.response?.data?.message ||
          'Không thể đánh dấu kiểm tra thanh toán'
      );
    }
  }
}

export default new TransactionService();
